"use client";

import { useState, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import Link from "@/components/Link";
import { trackEvent } from "@/lib/client/analytics";
import { setNagCooldown, isNagOnCooldown } from "@/lib/client/nagCooldown";
import {
  Modal as MantineModal,
  Button,
  Image,
  Stack,
  Text,
  Title,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { useAuthContext } from "@/context/authContext";
import styles from "./styles.module.scss";

const CTA_TITLES = [
  "Find it before everyone else does.",
  "Stop refreshing. Start getting alerts.",
  "The best pieces go fast.",
  "Sign up and never miss a deal.",
];

interface ProNagModalProps {
  isOpen: boolean;
  onClose: () => void;
  source:
    | "favorites_collections"
    | "saved_filters"
    | "scroll_nag"
    | "filter_switching"
    | "slidein_nag"
    | "private_collection"
    | "notification_bell"
    | "outbound_nag"
    | "item_revisit";
  titles?: string[];
  subtitle?: string;
  ctaLabel?: string;
}

export default function ProNagModal({
  isOpen,
  onClose,
  source,
  titles,
  subtitle = "Sign up free: saved filters, price drop alerts, and daily tokens included.",
  ctaLabel = "SIGN UP FREE",
}: ProNagModalProps) {
  const { isProUser } = useAuthContext();
  const isSmall = useMediaQuery({ query: "(min-width: 600px)" });
  const isMedium = useMediaQuery({ query: "(min-width: 850px)" });
  const modalSize = isMedium ? 600 : isSmall ? "calc(100vw - 64px)" : "calc(100vw - 80px)";
  const pool = titles ?? CTA_TITLES;
  const [title] = useState(() => pool[Math.floor(Math.random() * pool.length)]);
  const [shaking, setShaking] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    if (isNagOnCooldown()) {
      onCloseRef.current();
      return;
    }
    trackEvent("pro_nag_shown", "engagement", { cta_title: title, source });
  }, [isOpen, title, source]);

  const handleRealClose = () => {
    setNagCooldown();
    onClose();
  };

  const handleAttemptClose = () => {
    if (shaking) return;
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  };

  return (
    <MantineModal
      opened={isOpen && !isProUser}
      onClose={handleAttemptClose}
      withCloseButton={false}
      centered
      padding={0}
      size={modalSize}
      className={styles.modal}
      zIndex={9500}
      styles={{
        content: {
          border: "1px solid black",
        },
      }}
    >
      <MantineUnstyledButton
        onClick={handleRealClose}
        aria-label="Close modal"
        className={`${styles.closeButton} ${shaking ? styles.shake : ""}`}
      >
        <Image src="/cross.svg" alt="Close" w={20} h={20} />
      </MantineUnstyledButton>
      <Stack className={styles.content} align="center" gap="lg">
        <Title order={2} className={styles.cta}>
          {title}
        </Title>
        <Text className={styles.subtitle}>{subtitle}</Text>
        <Link
          href="/plans"
          onClick={() => {
            trackEvent("pro_nag_cta_clicked", "engagement", { cta_title: title, source });
            handleRealClose();
          }}
          className={styles.buttonLink}
        >
          <Button size="lg" variant="primary" fullWidth>
            {ctaLabel}
          </Button>
        </Link>
      </Stack>
    </MantineModal>
  );
}
